import React, { useLayoutEffect, useMemo } from 'react';
import { ZuneloProvider } from './context/ZuneloContext';
import { ThemeProvider } from './theme';
import { AuthProvider, LoginScreen, useAuth } from './auth';
import { MainLayout } from './features/layout/MainLayout';
import { AppErrorBoundary } from './ui/common/AppErrorBoundary';
import { CurrentUserIdentity } from './domain/types';
import { setApiIdTokenGetter, setSourceCacheUser } from './services/clients';

export { SIDEBAR_EXPANDED_PX, SIDEBAR_COLLAPSED_PX } from './features/layout/AppSidebar';

export const AuthGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--z-bg)',
          color: 'var(--z-dim)',
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  return <>{children}</>;
};

/**
 * Registers the Firebase ID token getter and source-cache uid namespace
 * before ZuneloProvider's hydration effect runs (parent useLayoutEffect
 * precedes child useEffect).
 */
export const ApiAuthBridge: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { user, getIdToken } = useAuth();

  useLayoutEffect(() => {
    setApiIdTokenGetter(() => getIdToken());
    setSourceCacheUser(user?.uid);
    return () => {
      setApiIdTokenGetter(undefined);
      setSourceCacheUser(undefined);
    };
  }, [getIdToken, user?.uid]);

  return <>{children}</>;
};

export const AuthenticatedApp: React.FC = () => {
  const { user } = useAuth();

  const currentUserIdentity = useMemo<CurrentUserIdentity | undefined>(() => {
    if (!user) return undefined;
    return {
      uid: user.uid,
      email: user.email ?? undefined,
      displayName: user.displayName ?? undefined,
    };
  }, [user]);

  return (
    <AuthGate>
      <ApiAuthBridge>
        <ZuneloProvider currentUser={currentUserIdentity}>
          <MainLayout />
        </ZuneloProvider>
      </ApiAuthBridge>
    </AuthGate>
  );
};

export const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppErrorBoundary variant="page">
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </AppErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
